import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { useSearchParams } from "react-router-dom";
import { googleCalendarUrl, outlookCalendarUrl } from "../lib/deepLinks";
import { Button } from "../components/ui/button";
import CategoryManager from "../components/CategoryManager";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Calendar as CalendarComponent } from "../components/ui/calendar";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Checkbox } from "../components/ui/checkbox";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  CalendarIcon,
  Loader2,
  MapPin,
  Clock,
  Users,
  Plus,
  FileText,
  Send,
  CheckCircle,
  RefreshCw,
  Download,
  Link,
  Unlink,
  Settings,
  CalendarDays,
  List,
  Pencil,
  Trash2,
  Eye,
  Shield,
  ClipboardList,
  Zap,
  Wrench,
  Sparkles,
  AlertTriangle,
  ChevronDown,
  Repeat,
  Copy,
  Briefcase,
  Link2,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { formatDate } from "../lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Quick Job Templates
const QUICK_JOB_TEMPLATES = [
  {
    id: "window_cleaning",
    name: "Window Cleaning",
    icon: Sparkles,
    title: "Window Cleaning Service",
    description: "Standard window cleaning service",
    priority: "normal",
    estimatedHours: 4,
  },
  {
    id: "maintenance",
    name: "General Maintenance",
    icon: Wrench,
    title: "General Maintenance",
    description: "Routine maintenance and repairs",
    priority: "normal",
    estimatedHours: 2,
  },
  {
    id: "emergency",
    name: "Emergency Repair",
    icon: AlertTriangle,
    title: "Emergency Repair",
    description: "Urgent repair required",
    priority: "urgent",
    estimatedHours: 3,
  },
  {
    id: "inspection",
    name: "Site Inspection",
    icon: Eye,
    title: "Site Inspection",
    description: "Property inspection and assessment",
    priority: "normal",
    estimatedHours: 1,
  },
];

// Google Calendar OAuth URL
const GOOGLE_AUTH_URL = "https://accounts.google.com/o/oauth2/v2/auth";
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";
const GOOGLE_SCOPES = "https://www.googleapis.com/auth/calendar.events";

// Microsoft OAuth URL
const MS_AUTH_URL = "https://login.microsoftonline.com/common/oauth2/v2.0/authorize";
const MS_CLIENT_ID = process.env.REACT_APP_MS_CLIENT_ID || "";
const MS_SCOPES = "openid profile email Calendars.ReadWrite";

const getMonday = (date = new Date()) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  return d.toISOString().split("T")[0];
};

const Calendar = () => {
  const { user, isOfficeManager } = useAuth();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "calendar";
  const [activeTab, setActiveTab] = useState(initialTab);

  // Phase 19.29.1 — handle redirect back from real Google Calendar OAuth flow.
  useEffect(() => {
    const startGoogleReconnect = async () => {
      try {
        const { default: axios } = await import("axios");
        const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
        const res = await axios.get(`${API}/oauth/calendar/connect`);
        if (res.data?.authorization_url) {
          window.location.href = res.data.authorization_url;
        }
      } catch (e) {
        toast.error("Could not start Google reconnect — please try again from Calendar Settings.");
      }
    };
    const startOutlookReconnect = async () => {
      try {
        const { default: axios } = await import("axios");
        const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
        const res = await axios.get(`${API}/oauth/outlook/connect`);
        if (res.data?.authorization_url) {
          window.location.href = res.data.authorization_url;
        }
      } catch (e) {
        toast.error("Could not start Outlook reconnect — please try again from Calendar Settings.");
      }
    };

    const refreshConnections = async () => {
      try {
        const { default: axios } = await import("axios");
        const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
        const res = await axios.get(`${API}/calendar/connections`);
        setConnections(res.data);
      } catch (e) {
        // Silent — banner will catch up on next page load.
      }
    };

    const googleConnectedParam = searchParams.get("google_connected") === "1";
    const googleWarning = searchParams.get("google_warning") === "calendar_scope_not_granted";
    const googleError = searchParams.get("google_error");
    const outlookConnectedParam = searchParams.get("outlook_connected") === "1";
    const outlookWarning = searchParams.get("outlook_warning") === "calendar_scope_not_granted";
    const outlookError = searchParams.get("outlook_error");
    const handledAny = googleConnectedParam || googleWarning || googleError || outlookConnectedParam || outlookWarning || outlookError;

    if (googleConnectedParam) {
      toast.success("Google Calendar connected successfully!");
    } else if (googleWarning) {
      toast.warning(
        "Google sign-in succeeded, but you didn't grant Calendar access. We can't read or write events without it.",
        {
          description: "Tick the calendar checkbox on the Google consent screen to fix this.",
          duration: Infinity,
          action: { label: "Reconnect", onClick: startGoogleReconnect },
          cancel: { label: "Dismiss", onClick: () => {} },
        }
      );
    } else if (googleError) {
      toast.error(`Google Calendar: ${googleError}`);
    }

    if (outlookConnectedParam) {
      toast.success("Outlook Calendar connected successfully!");
    } else if (outlookWarning) {
      toast.warning(
        "Outlook sign-in succeeded, but you didn't grant Calendar access. We can't read or write events without it.",
        {
          description: "Approve the Calendars.ReadWrite permission on the Microsoft consent screen to fix this.",
          duration: Infinity,
          action: { label: "Reconnect", onClick: startOutlookReconnect },
          cancel: { label: "Dismiss", onClick: () => {} },
        }
      );
    } else if (outlookError) {
      toast.error(`Outlook Calendar: ${outlookError}`);
    }

    if (handledAny) {
      window.history.replaceState({}, document.title, window.location.pathname);
      // Don't refetch on pure-error path (no token state changed); only on
      // connect-success or scope-warning paths.
      if (googleConnectedParam || googleWarning || outlookConnectedParam || outlookWarning) {
        refreshConnections();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const [schedules, setSchedules] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [jobTypes, setJobTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState("calendar"); // calendar or list
  
  // Calendar connections
  const [connections, setConnections] = useState([]);
  const [syncing, setSyncing] = useState(false);
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [jobCreationMode, setJobCreationMode] = useState("quote"); // "quote" or "standalone"
  const [selectedQuote, setSelectedQuote] = useState("");
  const [proposedDates, setProposedDates] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  
  // Recurring job state
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurrencePattern, setRecurrencePattern] = useState("weekly");
  const [recurrenceEndDate, setRecurrenceEndDate] = useState(null);
  const [generateOccurrences, setGenerateOccurrences] = useState(4);
  
  // Standalone job fields
  const [jobTitle, setJobTitle] = useState("");
  const [jobDescription, setJobDescription] = useState("");
  const [jobAddress, setJobAddress] = useState("");
  const [selectedClient, setSelectedClient] = useState("");
  const [selectedCompany, setSelectedCompany] = useState("");
  const [jobPriority, setJobPriority] = useState("normal");
  const [estimatedHours, setEstimatedHours] = useState("");
  const [estimatedCost, setEstimatedCost] = useState("");
  const [clients, setClients] = useState([]);
  const [companies, setCompanies] = useState([]);
  
  // Work notice dialog state
  const [noticeDialogOpen, setNoticeDialogOpen] = useState(false);
  const [selectedScheduleForNotice, setSelectedScheduleForNotice] = useState(null);
  const [noticePreview, setNoticePreview] = useState(null);
  const [loadingNotice, setLoadingNotice] = useState(false);
  const [sendingNotice, setSendingNotice] = useState(false);
  
  // Complete job dialog state
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [selectedScheduleForComplete, setSelectedScheduleForComplete] = useState(null);
  const [actualHours, setActualHours] = useState(0);
  const [autoUpdateQuote, setAutoUpdateQuote] = useState(true);
  const [completing, setCompleting] = useState(false);

  // Sync settings dialog
  const [syncSettingsOpen, setSyncSettingsOpen] = useState(false);
  const [syncCredentials, setSyncCredentials] = useState({
    google_client_id: "",
    google_client_secret: "",
    outlook_client_id: "",
    outlook_client_secret: "",
    outlook_tenant_id: "common",
  });
  const [savingSyncSettings, setSavingSyncSettings] = useState(false);

  // Edit job state
  const [editJobDialogOpen, setEditJobDialogOpen] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [editJobForm, setEditJobForm] = useState({
    title: "",
    description: "",
    address: "",
    priority: "normal",
    estimated_hours: "",
    notes: "",
    assigned_employees: [],
    scheduled_date: "",
  });

  // Weekly Summary State
  const [summaries, setSummaries] = useState([]);
  const [selectedWeek, setSelectedWeek] = useState(getMonday());
  const [selectedSummaryEmployee, setSelectedSummaryEmployee] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [summarySettings, setSummarySettings] = useState({
    auto_send_enabled: false,
    auto_send_time: "08:00",
    auto_send_day: "monday",
    include_safety_docs: true,
    require_acknowledgment: true,
    email_delivery: true,
    in_app_delivery: true,
  });
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedSummary, setSelectedSummary] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [editNotes, setEditNotes] = useState("");
  const [generating, setGenerating] = useState(false);
  const [sendingSummary, setSendingSummary] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      // Phase 19.13: Portal users (employee_portal/client_portal) shouldn't reach
      // /calendar in normal flow (Layout guard redirects them), but be defensive:
      // skip the /clients fetch which 403s for portal users without can_view_clients.
      const isPortalUser = user?.account_type === "employee_portal" || user?.account_type === "client_portal";
      const [schedulesRes, quotesRes, employeesRes, summariesRes, settingsRes, clientsRes, companiesRes, jobTypesRes] = await Promise.all([
        axios.get(`${API}/schedules`),
        axios.get(`${API}/quotes?status=approved`),
        axios.get(`${API}/employees?current_only=true`),
        axios.get(`${API}/weekly-summary`).catch(() => ({ data: [] })),
        axios.get(`${API}/weekly-summary/settings`).catch(() => ({ data: summarySettings })),
        isPortalUser ? Promise.resolve({ data: [] }) : axios.get(`${API}/clients`).catch(() => ({ data: [] })),
        isPortalUser ? Promise.resolve({ data: [] }) : axios.get(`${API}/companies`).catch(() => ({ data: [] })),
        axios.get(`${API}/categories/?category_type=job_type`).catch(() => ({ data: [] })),
      ]);
      setSchedules(schedulesRes.data);
      setQuotes(quotesRes.data);
      setEmployees(employeesRes.data);
      setSummaries(summariesRes.data);
      setSummarySettings(settingsRes.data);
      setClients(clientsRes.data);
      setCompanies(companiesRes.data);
      setJobTypes(jobTypesRes.data || []);
    } catch (error) {
      toast.error("Failed to fetch data");
    }
  }, [user?.account_type]);

  const fetchConnections = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/calendar/connections`);
      setConnections(response.data);
    } catch (error) {
      console.error("Failed to fetch connections", error);
    }
  }, []);

  const handleOAuthCallback = useCallback(async () => {
    // Phase 19.32 — legacy mock-OAuth callback (kept for back-compat with any
    // old test deep-links). Real OAuth now redirects to /api/oauth/*/callback
    // on the backend, which lands the user back on /calendar?...connected=1.
    // No-op intentionally.
  }, []);

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([fetchData(), fetchConnections()]);
      setLoading(false);
    };
    loadAll();
  }, [fetchData, fetchConnections, handleOAuthCallback]);

  const handleCreateSchedule = async () => {
    if (jobCreationMode === "quote") {
      // Existing quote-based scheduling
      if (!selectedQuote || proposedDates.length === 0) {
        toast.error("Please select a quote and at least one date");
        return;
      }

      setSaving(true);
      try {
        await axios.post(`${API}/schedules`, {
          quote_id: selectedQuote,
          proposed_dates: proposedDates.map((d) => d.toISOString().split("T")[0]),
          assigned_employees: selectedEmployees,
          notes,
        });
        toast.success("Schedule created from quote");
        setDialogOpen(false);
        resetForm();
        fetchData();
      } catch (error) {
        toast.error(error.response?.data?.detail || "Failed to create schedule");
      } finally {
        setSaving(false);
      }
    } else {
      // Standalone job creation
      if (!jobTitle || proposedDates.length === 0) {
        toast.error("Please enter a job title and at least one date");
        return;
      }

      setSaving(true);
      try {
        await axios.post(`${API}/schedules`, {
          title: jobTitle,
          description: jobDescription,
          property_address: jobAddress,
          client_id: selectedClient || undefined,
          company_id: selectedCompany || undefined,
          job_type: "standalone",
          priority: jobPriority,
          estimated_hours: estimatedHours ? parseFloat(estimatedHours) : undefined,
          estimated_cost: estimatedCost ? parseFloat(estimatedCost) : undefined,
          proposed_dates: proposedDates.map((d) => d.toISOString().split("T")[0]),
          assigned_employees: selectedEmployees,
          notes,
          // Recurring job fields
          is_recurring: isRecurring,
          recurrence_pattern: isRecurring ? recurrencePattern : undefined,
          recurrence_end_date: isRecurring && recurrenceEndDate ? recurrenceEndDate.toISOString().split("T")[0] : undefined,
          generate_occurrences: isRecurring ? generateOccurrences : 0,
        });
        toast.success(isRecurring 
          ? `Recurring job created with ${generateOccurrences} occurrences` 
          : "Standalone job created successfully"
        );
        setDialogOpen(false);
        resetForm();
        fetchData();
      } catch (error) {
        toast.error(error.response?.data?.detail || "Failed to create job");
      } finally {
        setSaving(false);
      }
    }
  };

  const handleConfirmSchedule = async (scheduleId, date) => {
    try {
      await axios.patch(`${API}/schedules/${scheduleId}/confirm?confirmed_date=${date}`);
      toast.success("Schedule confirmed");
      fetchData();
    } catch (error) {
      toast.error("Failed to confirm schedule");
    }
  };

  // Quick Job - creates job from template with selected date
  const handleQuickJob = async (template) => {
    if (!selectedDate) {
      toast.error("Please select a date first");
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API}/schedules`, {
        title: template.title,
        description: template.description,
        job_type: "standalone",
        priority: template.priority,
        estimated_hours: template.estimatedHours,
        proposed_dates: [selectedDate.toISOString().split("T")[0]],
      });
      toast.success(`Quick job "${template.name}" created for ${formatDate(selectedDate.toISOString())}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create quick job");
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setJobCreationMode("quote");
    setSelectedQuote("");
    setProposedDates([]);
    setSelectedEmployees([]);
    setNotes("");
    // Standalone fields
    setJobTitle("");
    setJobDescription("");
    setJobAddress("");
    setSelectedClient("");
    setSelectedCompany("");
    setJobPriority("normal");
    setEstimatedHours("");
    setEstimatedCost("");
    // Recurring fields
    setIsRecurring(false);
    setRecurrencePattern("weekly");
    setRecurrenceEndDate(null);
    setGenerateOccurrences(4);
  };

  const getSchedulesForDate = (date) => {
    const dateStr = date.toISOString().split("T")[0];
    return schedules.filter(
      (s) =>
        s.proposed_dates?.includes(dateStr) || s.confirmed_date === dateStr
    );
  };

  const toggleEmployee = (empId) => {
    setSelectedEmployees((prev) =>
      prev.includes(empId)
        ? prev.filter((id) => id !== empId)
        : [...prev, empId]
    );
  };

  const handleDuplicateJob = async (scheduleId) => {
    try {
      const newDate = selectedDate ? selectedDate.toISOString().split("T")[0] : undefined;
      const response = await axios.post(`${API}/schedules/${scheduleId}/duplicate${newDate ? `?new_date=${newDate}` : ''}`);
      toast.success(`Job duplicated: ${response.data.title}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to duplicate job");
    }
  };

  const handleGenerateNotice = async (schedule) => {
    setSelectedScheduleForNotice(schedule);
    setLoadingNotice(true);
    setNoticeDialogOpen(true);
    
    try {
      const response = await axios.get(`${API}/schedules/${schedule.id}/generate-notice`);
      setNoticePreview(response.data);
    } catch (error) {
      toast.error("Failed to generate notice");
    } finally {
      setLoadingNotice(false);
    }
  };

  const handleSendNotice = async () => {
    if (!selectedScheduleForNotice || !noticePreview) return;
    
    setSendingNotice(true);
    try {
      await axios.post(`${API}/scheduled-documents`, {
        template_id: noticePreview.template_id,
        schedule_id: selectedScheduleForNotice.id,
        send_days_before: 0,
        require_review: false,
      });
      
      const docsRes = await axios.get(`${API}/scheduled-documents`);
      const newDoc = docsRes.data.find(d => d.schedule_id === selectedScheduleForNotice.id && d.status === "pending");
      
      if (newDoc) {
        await axios.post(`${API}/scheduled-documents/${newDoc.id}/send`);
      }
      
      toast.success("Work notice sent!");
      setNoticeDialogOpen(false);
    } catch (error) {
      toast.error("Failed to send notice");
    } finally {
      setSendingNotice(false);
    }
  };

  const handleOpenCompleteDialog = (schedule) => {
    setSelectedScheduleForComplete(schedule);
    const quote = quotes.find(q => q.id === schedule.quote_id);
    setActualHours(quote?.piecework_hours || 0);
    setAutoUpdateQuote(true);
    setCompleteDialogOpen(true);
  };

  const handleCompleteJob = async () => {
    if (!selectedScheduleForComplete) return;
    
    setCompleting(true);
    try {
      await axios.patch(
        `${API}/schedules/${selectedScheduleForComplete.id}/complete?actual_hours=${actualHours}&auto_update_quote=${autoUpdateQuote}`
      );
      toast.success("Job marked as complete!");
      setCompleteDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to complete job");
    } finally {
      setCompleting(false);
    }
  };

  // Calendar sync functions
  // Phase 19.29.1 — uses the platform's pre-configured Google OAuth client (server-side .env).
  // Customers do NOT supply their own credentials — they just click and authorize their Google account.
  const connectGoogleCalendar = async () => {
    try {
      const res = await axios.get(`${API}/oauth/calendar/connect`);
      if (res.data?.authorization_url) {
        window.location.href = res.data.authorization_url;
      } else {
        toast.error("Failed to start Google OAuth");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start Google sign-in");
    }
  };

  const connectOutlookCalendar = async () => {
    try {
      const res = await axios.get(`${API}/oauth/outlook/connect`);
      if (res.data?.authorization_url) {
        window.location.href = res.data.authorization_url;
      } else {
        toast.error("Failed to start Outlook OAuth");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start Microsoft sign-in");
    }
  };

  const handleSaveSyncCredentials = async () => {
    setSavingSyncSettings(true);
    try {
      await axios.post(`${API}/calendar/sync-credentials`, syncCredentials);
      toast.success("Calendar credentials saved successfully");
    } catch (error) {
      toast.error("Failed to save credentials");
    } finally {
      setSavingSyncSettings(false);
    }
  };

  const handleEditJob = (schedule) => {
    setEditingJob(schedule);
    setEditJobForm({
      title: schedule.title || "",
      description: schedule.description || "",
      address: schedule.address || "",
      priority: schedule.priority || "normal",
      estimated_hours: schedule.estimated_hours?.toString() || "",
      notes: schedule.notes || "",
      assigned_employees: schedule.assigned_employee_ids || [],
      scheduled_date: schedule.proposed_dates?.[0] || "",
    });
    setEditJobDialogOpen(true);
  };

  const handleSaveEditJob = async () => {
    if (!editingJob) return;
    setSaving(true);
    try {
      await axios.put(`${API}/schedules/${editingJob.id}`, {
        title: editJobForm.title,
        description: editJobForm.description,
        address: editJobForm.address,
        priority: editJobForm.priority,
        estimated_hours: editJobForm.estimated_hours ? parseFloat(editJobForm.estimated_hours) : null,
        notes: editJobForm.notes,
        assigned_employee_ids: editJobForm.assigned_employees,
        proposed_dates: editJobForm.scheduled_date ? [editJobForm.scheduled_date] : [],
      });
      toast.success("Job updated successfully");
      setEditJobDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update job");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteJob = async (scheduleId) => {
    if (!window.confirm("Are you sure you want to delete this job?")) return;
    try {
      await axios.delete(`${API}/schedules/${scheduleId}`);
      toast.success("Job deleted");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete job");
    }
  };

  const disconnectCalendar = async (connectionId) => {
    if (!window.confirm("Are you sure you want to disconnect this calendar?")) return;
    try {
      await axios.delete(`${API}/calendar/connections/${connectionId}`);
      toast.success("Calendar disconnected");
      fetchConnections();
    } catch (error) {
      toast.error("Failed to disconnect calendar");
    }
  };

  const syncCalendar = async (connectionId) => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/calendar/sync?connection_id=${connectionId}`);
      toast.success(response.data.message);
      fetchConnections();
    } catch (error) {
      toast.error("Failed to sync calendar");
    } finally {
      setSyncing(false);
    }
  };

  const handleExportICS = async () => {
    try {
      const response = await axios.get(`${API}/calendar/export/ics`, {
        responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "schedule.ics");
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Calendar exported! Import this file into Google Calendar or Outlook.");
    } catch (error) {
      toast.error("Failed to export calendar");
    }
  };

  // Weekly Summary Functions
  const handleGenerateSummary = async () => {
    if (!selectedSummaryEmployee) {
      toast.error("Please select an employee");
      return;
    }
    setGenerating(true);
    try {
      const response = await axios.post(
        `${API}/weekly-summary/generate?employee_id=${selectedSummaryEmployee}&week_start=${selectedWeek}`
      );
      toast.success("Summary generated");
      fetchData();
      setPreviewData(response.data);
      setPreviewDialogOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateAll = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/weekly-summary/generate-all?week_start=${selectedWeek}`);
      toast.success(`Generated ${response.data.generated_count} summaries`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const handleViewSummary = async (summaryId) => {
    try {
      const response = await axios.get(`${API}/weekly-summary/${summaryId}`);
      setPreviewData(response.data);
      setPreviewDialogOpen(true);
    } catch (error) {
      toast.error("Failed to load summary");
    }
  };

  const handleEditSummary = (summary) => {
    setSelectedSummary(summary);
    setEditNotes(summary.notes || "");
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedSummary) return;
    try {
      await axios.put(
        `${API}/weekly-summary/${selectedSummary.id}?notes=${encodeURIComponent(editNotes)}`
      );
      toast.success("Summary updated");
      setEditDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to update summary");
    }
  };

  const handleSendSummary = async (summaryId) => {
    if (!window.confirm("Send this summary to the employee?")) return;
    setSendingSummary(true);
    try {
      await axios.post(`${API}/weekly-summary/${summaryId}/send`);
      toast.success("Summary sent");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Send failed");
    } finally {
      setSendingSummary(false);
    }
  };

  const handleDownloadPDF = (summaryId) => {
    window.open(`${API}/weekly-summary/${summaryId}/pdf`, "_blank");
  };

  const handleSaveSummarySettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(`${API}/weekly-summary/settings`, summarySettings);
      toast.success("Settings saved");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSavingSettings(false);
    }
  };

  const getSummaryStatusBadge = (status) => {
    switch (status) {
      case "draft":
        return <Badge variant="outline"><Clock className="h-3 w-3 mr-1" />Draft</Badge>;
      case "pending_review":
        return <Badge className="bg-yellow-500"><Eye className="h-3 w-3 mr-1" />Pending Review</Badge>;
      case "sent":
        return <Badge className="bg-blue-500"><Send className="h-3 w-3 mr-1" />Sent</Badge>;
      case "acknowledged":
        return <Badge className="bg-emerald-500"><CheckCircle className="h-3 w-3 mr-1" />Acknowledged</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const filteredSummaries = summaries.filter((s) => {
    const matchesStatus = !filterStatus || s.status === filterStatus;
    return matchesStatus;
  });

  const scheduledDates = schedules.flatMap((s) => [
    ...s.proposed_dates.map((d) => new Date(d)),
    s.confirmed_date ? new Date(s.confirmed_date) : null,
  ].filter(Boolean));

  const googleConnected = connections.find(c => c.provider === "google");
  const outlookConnected = connections.find(c => c.provider === "outlook");

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in" data-testid="calendar-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight flex items-center gap-2">
            <CalendarIcon className="h-8 w-8 text-primary" />
            Calendar
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage schedules and sync with external calendars
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={connectGoogleCalendar} data-testid="calendar-connect-google-btn">
            <Link2 className="h-4 w-4 mr-2" />
            Connect Google Calendar
          </Button>
          <Button variant="outline" onClick={connectOutlookCalendar} data-testid="calendar-connect-outlook-btn">
            <Link2 className="h-4 w-4 mr-2" />
            Connect Outlook Calendar
          </Button>
          <Button variant="outline" onClick={handleExportICS} data-testid="export-ics-btn">
            <Download className="h-4 w-4 mr-2" />
            Export ICS
          </Button>
          {isOfficeManager && (
            <>
              {/* Quick Job Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" disabled={saving} data-testid="quick-job-btn">
                    <Zap className="h-4 w-4 mr-2" />
                    Quick Job
                    <ChevronDown className="h-4 w-4 ml-2" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    {selectedDate 
                      ? `Create job for ${formatDate(selectedDate.toISOString())}`
                      : "Select a date first"
                    }
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {/* Use dynamic job types if available, fallback to static templates */}
                  {(jobTypes.length > 0 ? jobTypes.map(jt => ({
                    id: jt.name?.toLowerCase().replace(/\s+/g, '_'),
                    name: jt.name,
                    icon: Briefcase,
                    title: jt.name,
                    description: `${jt.name} service`,
                    priority: "normal",
                    estimatedHours: 2,
                  })) : QUICK_JOB_TEMPLATES).map((template) => {
                    const Icon = template.icon;
                    return (
                      <DropdownMenuItem
                        key={template.id}
                        onClick={() => handleQuickJob(template)}
                        disabled={!selectedDate || saving}
                        className="cursor-pointer"
                      >
                        <Icon className="h-4 w-4 mr-2" />
                        <div className="flex flex-col">
                          <span>{template.name}</span>
                          <span className="text-xs text-muted-foreground">
                            ~{template.estimatedHours}h • {template.priority}
                          </span>
                        </div>
                      </DropdownMenuItem>
                    );
                  })}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={() => {
                      // Open category manager for job types
                      window.dispatchEvent(new CustomEvent('openJobTypeManager'));
                    }}
                    className="cursor-pointer text-primary"
                  >
                    <Settings className="h-4 w-4 mr-2" />
                    Manage Job Types
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              
              <Button onClick={() => setDialogOpen(true)} data-testid="schedule-job-btn">
                <Plus className="h-4 w-4 mr-2" />
                Schedule Job
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Quick Sync Status */}
      <div className="flex flex-wrap gap-3">
        {googleConnected ? (
          <Badge variant="outline" className="flex items-center gap-2 px-3 py-1.5" data-testid="google-status-badge">
            <div className={`w-2 h-2 rounded-full ${googleConnected.scope_ok === false ? "bg-amber-500" : "bg-emerald-500"}`} />
            {googleConnected.scope_ok === false ? "Google: Permission missing" : "Google Calendar Connected"}
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-5 px-1 ml-1" 
              onClick={() => syncCalendar(googleConnected.id)}
              disabled={syncing}
            >
              <RefreshCw className={`h-3 w-3 ${syncing ? "animate-spin" : ""}`} />
            </Button>
          </Badge>
        ) : (
          <Badge variant="outline" className="flex items-center gap-2 px-3 py-1.5 text-muted-foreground">
            <div className="w-2 h-2 rounded-full bg-gray-400" />
            Google Calendar Not Connected
          </Badge>
        )}
        {outlookConnected ? (
          <Badge variant="outline" className="flex items-center gap-2 px-3 py-1.5" data-testid="outlook-status-badge">
            <div className={`w-2 h-2 rounded-full ${outlookConnected.scope_ok === false ? "bg-amber-500" : "bg-emerald-500"}`} />
            {outlookConnected.scope_ok === false ? "Outlook: Permission missing" : "Outlook Connected"}
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-5 px-1 ml-1" 
              onClick={() => syncCalendar(outlookConnected.id)}
              disabled={syncing}
            >
              <RefreshCw className={`h-3 w-3 ${syncing ? "animate-spin" : ""}`} />
            </Button>
          </Badge>
        ) : (
          <Badge variant="outline" className="flex items-center gap-2 px-3 py-1.5 text-muted-foreground">
            <div className="w-2 h-2 rounded-full bg-gray-400" />
            Outlook Not Connected
          </Badge>
        )}
      </div>

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="calendar" className="flex items-center gap-2" data-testid="tab-calendar">
            <CalendarDays className="h-4 w-4" />
            Calendar View
          </TabsTrigger>
          <TabsTrigger value="list" className="flex items-center gap-2" data-testid="tab-list">
            <List className="h-4 w-4" />
            List View
          </TabsTrigger>
          <TabsTrigger value="weekly-summary" className="flex items-center gap-2" data-testid="tab-weekly-summary">
            <ClipboardList className="h-4 w-4" />
            Weekly Summary
          </TabsTrigger>
        </TabsList>

        <TabsContent value="calendar" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Calendar */}
            <Card className="lg:col-span-1">
              <CardHeader>
                <CardTitle className="text-lg">Select Date</CardTitle>
              </CardHeader>
              <CardContent>
                <CalendarComponent
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  modifiers={{
                    scheduled: scheduledDates,
                  }}
                  modifiersStyles={{
                    scheduled: {
                      backgroundColor: "hsl(var(--primary))",
                      color: "hsl(var(--primary-foreground))",
                      borderRadius: "50%",
                    },
                  }}
                  className="rounded-md border p-2 w-full"
                  classNames={{
                    months: "flex flex-col w-full",
                    month: "space-y-2 w-full",
                    table: "w-full border-collapse",
                    head_row: "grid grid-cols-7 w-full",
                    head_cell: "text-muted-foreground font-normal text-[0.75rem] text-center",
                    row: "grid grid-cols-7 w-full mt-1",
                    cell: "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-accent [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected])]:rounded-md",
                    day: "h-8 w-full p-0 font-normal aria-selected:opacity-100 hover:bg-accent hover:text-accent-foreground rounded-md inline-flex items-center justify-center",
                  }}
                />
                <div className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
                  <div className="w-3 h-3 rounded-full bg-primary" />
                  <span>Scheduled jobs</span>
                </div>
              </CardContent>
            </Card>

            {/* Jobs for Selected Date */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>
                  {selectedDate
                    ? `Jobs on ${formatDate(selectedDate.toISOString())}`
                    : "Select a date"}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {selectedDate && getSchedulesForDate(selectedDate).length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <CalendarIcon className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No jobs scheduled for this date</p>
                    {isOfficeManager && (
                      <Button
                        variant="link"
                        onClick={() => setDialogOpen(true)}
                        className="mt-2"
                      >
                        Schedule a job
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {getSchedulesForDate(selectedDate).map((schedule) => {
                      const quote = quotes.find((q) => q.id === schedule.quote_id);
                      const isStandalone = schedule.job_type === "standalone" || !schedule.quote_id;
                      return (
                        <div
                          key={schedule.id}
                          className="p-4 border rounded-lg space-y-3 hover:border-primary/50 transition-colors"
                          data-testid={`schedule-card-${schedule.id}`}
                        >
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <Badge
                                  variant="outline"
                                  className={
                                    schedule.status === "confirmed"
                                      ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/30"
                                      : schedule.status === "completed"
                                      ? "bg-blue-500/10 text-blue-600 border-blue-500/30"
                                      : "bg-amber-500/10 text-amber-600 border-amber-500/30"
                                  }
                                >
                                  {schedule.status}
                                </Badge>
                                {isStandalone ? (
                                  <Badge variant="secondary" className="text-xs">
                                    Standalone
                                  </Badge>
                                ) : quote && (
                                  <span className="font-mono text-sm">
                                    {quote.quote_number}
                                  </span>
                                )}
                                {schedule.priority && schedule.priority !== "normal" && (
                                  <Badge 
                                    variant="outline" 
                                    className={
                                      schedule.priority === "urgent" 
                                        ? "bg-red-500/10 text-red-600 border-red-500/30"
                                        : schedule.priority === "high"
                                        ? "bg-orange-500/10 text-orange-600 border-orange-500/30"
                                        : "bg-gray-500/10 text-gray-600"
                                    }
                                  >
                                    {schedule.priority}
                                  </Badge>
                                )}
                                {(schedule.is_recurring || schedule.parent_schedule_id) && (
                                  <Badge variant="outline" className="text-xs bg-purple-500/10 text-purple-600 border-purple-500/30">
                                    <Repeat className="h-3 w-3 mr-1" />
                                    Recurring
                                  </Badge>
                                )}
                              </div>
                              {/* Job title or address */}
                              <p className="font-medium mt-1 flex items-center gap-1">
                                <MapPin className="h-4 w-4" />
                                {schedule.title || (quote?.property_address) || schedule.property_address || "No address"}
                              </p>
                              {schedule.description && (
                                <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                                  {schedule.description}
                                </p>
                              )}
                              {/* Client/Company info for standalone jobs */}
                              {isStandalone && (schedule.client_name || schedule.company_name) && (
                                <p className="text-sm text-muted-foreground mt-1">
                                  {schedule.client_name && `Client: ${schedule.client_name}`}
                                  {schedule.client_name && schedule.company_name && " • "}
                                  {schedule.company_name && `Company: ${schedule.company_name}`}
                                </p>
                              )}
                            </div>
                          </div>

                          <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                            <div className="flex items-center gap-1">
                              <Clock className="h-4 w-4" />
                              {schedule.confirmed_date ? (
                                <span>
                                  Confirmed: {formatDate(schedule.confirmed_date)}
                                </span>
                              ) : (
                                <span>
                                  Proposed:{" "}
                                  {schedule.proposed_dates?.map(formatDate).join(", ")}
                                </span>
                              )}
                            </div>
                            {schedule.estimated_hours && (
                              <div className="flex items-center gap-1">
                                <Clock className="h-4 w-4" />
                                Est: {schedule.estimated_hours}h
                              </div>
                            )}
                            {schedule.estimated_cost && (
                              <div className="flex items-center gap-1">
                                ${parseFloat(schedule.estimated_cost).toFixed(2)}
                              </div>
                            )}
                            {schedule.assigned_employees?.length > 0 && (
                              <div className="flex items-center gap-1">
                                <Users className="h-4 w-4" />
                                {schedule.assigned_employees.length} assigned
                              </div>
                            )}
                          </div>

                          {isOfficeManager &&
                            schedule.status === "proposed" &&
                            schedule.proposed_dates.length > 0 && (
                              <div className="flex gap-2 pt-2 border-t">
                                <span className="text-sm text-muted-foreground">
                                  Confirm date:
                                </span>
                                {schedule.proposed_dates.map((date) => (
                                  <Button
                                    key={date}
                                    variant="outline"
                                    size="sm"
                                    onClick={() =>
                                      handleConfirmSchedule(schedule.id, date)
                                    }
                                  >
                                    {formatDate(date)}
                                  </Button>
                                ))}
                              </div>
                            )}
                          
                          {isOfficeManager && (
                            <div className="flex flex-wrap gap-2 pt-2 border-t">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleEditJob(schedule)}
                              >
                                <Pencil className="h-4 w-4 mr-1" />
                                Edit
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleGenerateNotice(schedule)}
                              >
                                <FileText className="h-4 w-4 mr-1" />
                                Work Notice
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleOpenCompleteDialog(schedule)}
                              >
                                <CheckCircle className="h-4 w-4 mr-1" />
                                Complete Job
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDuplicateJob(schedule.id)}
                                title="Duplicate this job"
                              >
                                <Copy className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-destructive hover:text-destructive"
                                onClick={() => handleDeleteJob(schedule.id)}
                                title="Delete this job"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                          
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="list" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>All Scheduled Jobs</CardTitle>
              <CardDescription>
                {schedules.length} job{schedules.length !== 1 ? "s" : ""} scheduled
              </CardDescription>
            </CardHeader>
            <CardContent>
              {schedules.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <CalendarIcon className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No scheduled jobs yet</p>
                  {isOfficeManager && quotes.length > 0 && (
                    <Button
                      variant="link"
                      onClick={() => setDialogOpen(true)}
                      className="mt-2"
                    >
                      Schedule your first job
                    </Button>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  {schedules
                    .sort((a, b) => {
                      const dateA = a.confirmed_date || a.proposed_dates[0];
                      const dateB = b.confirmed_date || b.proposed_dates[0];
                      return new Date(dateA) - new Date(dateB);
                    })
                    .map((schedule) => {
                      const quote = quotes.find((q) => q.id === schedule.quote_id);
                      const isStandalone = schedule.job_type === "standalone" || !schedule.quote_id;
                      return (
                        <div
                          key={schedule.id}
                          className="p-4 border rounded-lg space-y-3"
                        >
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <Badge
                                  variant="outline"
                                  className={
                                    schedule.status === "confirmed"
                                      ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/30"
                                      : schedule.status === "completed"
                                      ? "bg-blue-500/10 text-blue-600 border-blue-500/30"
                                      : "bg-amber-500/10 text-amber-600 border-amber-500/30"
                                  }
                                >
                                  {schedule.status}
                                </Badge>
                                {isStandalone ? (
                                  <Badge variant="secondary" className="text-xs">
                                    Standalone
                                  </Badge>
                                ) : quote && (
                                  <span className="font-mono text-sm">
                                    {quote.quote_number}
                                  </span>
                                )}
                                {schedule.priority && schedule.priority !== "normal" && (
                                  <Badge 
                                    variant="outline" 
                                    className={
                                      schedule.priority === "urgent" 
                                        ? "bg-red-500/10 text-red-600 border-red-500/30"
                                        : schedule.priority === "high"
                                        ? "bg-orange-500/10 text-orange-600 border-orange-500/30"
                                        : "bg-gray-500/10 text-gray-600"
                                    }
                                  >
                                    {schedule.priority}
                                  </Badge>
                                )}
                                {(schedule.is_recurring || schedule.parent_schedule_id) && (
                                  <Badge variant="outline" className="text-xs bg-purple-500/10 text-purple-600 border-purple-500/30">
                                    <Repeat className="h-3 w-3 mr-1" />
                                    Recurring
                                  </Badge>
                                )}
                              </div>
                              <p className="font-medium mt-1 flex items-center gap-1">
                                <MapPin className="h-4 w-4" />
                                {schedule.title || (quote?.property_address) || schedule.property_address || "No address"}
                              </p>
                              {schedule.description && (
                                <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                                  {schedule.description}
                                </p>
                              )}
                              {isStandalone && (schedule.client_name || schedule.company_name) && (
                                <p className="text-sm text-muted-foreground mt-1">
                                  {schedule.client_name && `Client: ${schedule.client_name}`}
                                  {schedule.client_name && schedule.company_name && " • "}
                                  {schedule.company_name && `Company: ${schedule.company_name}`}
                                </p>
                              )}
                            </div>
                            <div className="text-right">
                              <p className="font-medium">
                                {formatDate(schedule.confirmed_date || schedule.proposed_dates[0])}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {schedule.confirmed_date ? "Confirmed" : "Proposed"}
                              </p>
                            </div>
                          </div>

                          {schedule.assigned_employees?.length > 0 && (
                            <div className="flex items-center gap-1 text-sm text-muted-foreground">
                              <Users className="h-4 w-4" />
                              {schedule.assigned_employees.length} employee{schedule.assigned_employees.length !== 1 ? "s" : ""} assigned
                            </div>
                          )}

                          {/* Show estimated hours and cost for standalone jobs */}
                          {(schedule.estimated_hours || schedule.estimated_cost) && (
                            <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                              {schedule.estimated_hours && (
                                <div className="flex items-center gap-1">
                                  <Clock className="h-4 w-4" />
                                  Est: {schedule.estimated_hours}h
                                </div>
                              )}
                              {schedule.estimated_cost && (
                                <div className="flex items-center gap-1">
                                  ${parseFloat(schedule.estimated_cost).toFixed(2)}
                                </div>
                              )}
                            </div>
                          )}

                          {isOfficeManager &&
                            schedule.status === "proposed" &&
                            schedule.proposed_dates.length > 0 && (
                              <div className="flex gap-2 pt-2 border-t">
                                <span className="text-sm text-muted-foreground">
                                  Confirm:
                                </span>
                                {schedule.proposed_dates.map((date) => (
                                  <Button
                                    key={date}
                                    variant="outline"
                                    size="sm"
                                    onClick={() =>
                                      handleConfirmSchedule(schedule.id, date)
                                    }
                                  >
                                    {formatDate(date)}
                                  </Button>
                                ))}
                              </div>
                            )}
                          
                          {isOfficeManager && (
                            <div className="flex flex-wrap gap-2 pt-2 border-t">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleEditJob(schedule)}
                              >
                                <Pencil className="h-4 w-4 mr-1" />
                                Edit
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleGenerateNotice(schedule)}
                              >
                                <FileText className="h-4 w-4 mr-1" />
                                Work Notice
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleOpenCompleteDialog(schedule)}
                              >
                                <CheckCircle className="h-4 w-4 mr-1" />
                                Complete Job
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDuplicateJob(schedule.id)}
                                title="Duplicate this job"
                              >
                                <Copy className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-destructive hover:text-destructive"
                                onClick={() => handleDeleteJob(schedule.id)}
                                title="Delete this job"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Weekly Summary Tab */}
        <TabsContent value="weekly-summary" className="mt-4 space-y-4" data-testid="weekly-summary-content">
          {/* Generate Section */}
          {isOfficeManager && (
            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Generate Individual Summary</CardTitle>
                  <CardDescription>Create a summary for a specific employee</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Week Starting (Monday)</Label>
                    <Input
                      type="date"
                      value={selectedWeek}
                      onChange={(e) => setSelectedWeek(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Employee</Label>
                    <Select value={selectedSummaryEmployee} onValueChange={setSelectedSummaryEmployee}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select employee..." />
                      </SelectTrigger>
                      <SelectContent>
                        {employees.map((emp) => (
                          <SelectItem key={emp.id} value={emp.id}>
                            {emp.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    onClick={handleGenerateSummary}
                    disabled={generating || !selectedSummaryEmployee}
                    className="w-full"
                    data-testid="generate-summary-btn"
                  >
                    {generating ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Plus className="h-4 w-4 mr-2" />
                    )}
                    Generate Summary
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Generate All Summaries</CardTitle>
                  <CardDescription>
                    Create summaries for all employees with scheduled jobs
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Week Starting (Monday)</Label>
                    <Input
                      type="date"
                      value={selectedWeek}
                      onChange={(e) => setSelectedWeek(e.target.value)}
                    />
                  </div>
                  <div className="p-4 bg-muted rounded-lg">
                    <p className="text-sm text-muted-foreground">
                      This will generate summaries for all employees who have jobs scheduled
                      for the selected week. Existing summaries will be updated.
                    </p>
                  </div>
                  <Button
                    onClick={handleGenerateAll}
                    disabled={generating}
                    variant="outline"
                    className="w-full"
                    data-testid="generate-all-btn"
                  >
                    {generating ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4 mr-2" />
                    )}
                    Generate All Summaries
                  </Button>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Summaries List */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Weekly Job Summaries</CardTitle>
                <Select value={filterStatus || "all"} onValueChange={(v) => setFilterStatus(v === "all" ? "" : v)}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="All Statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="pending_review">Pending Review</SelectItem>
                    <SelectItem value="sent">Sent</SelectItem>
                    <SelectItem value="acknowledged">Acknowledged</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead>Week</TableHead>
                    <TableHead>Jobs</TableHead>
                    <TableHead>Safety Docs</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-40">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSummaries.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        No summaries found. Generate one using the form above.
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredSummaries.map((summary) => (
                      <TableRow key={summary.id}>
                        <TableCell className="font-medium">{summary.employee_name}</TableCell>
                        <TableCell>
                          {summary.week_start} to {summary.week_end}
                        </TableCell>
                        <TableCell>{summary.schedules?.length || 0}</TableCell>
                        <TableCell>{summary.safety_document_ids?.length || 0}</TableCell>
                        <TableCell>{getSummaryStatusBadge(summary.status)}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" onClick={() => handleViewSummary(summary.id)} data-testid={`view-summary-${summary.id}`}>
                              <Eye className="h-4 w-4" />
                            </Button>
                            {summary.status === "draft" && isOfficeManager && (
                              <>
                                <Button variant="ghost" size="icon" onClick={() => handleEditSummary(summary)}>
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleSendSummary(summary.id)}
                                  disabled={sendingSummary}
                                >
                                  <Send className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                            <Button variant="ghost" size="icon" onClick={() => handleDownloadPDF(summary.id)}>
                              <Download className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Auto-Send Settings for Office Managers */}
          {isOfficeManager && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Auto-Send Settings</CardTitle>
                <CardDescription>
                  Configure automatic weekly summary generation and sending
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <p className="font-medium">Enable Auto-Send</p>
                    <p className="text-sm text-muted-foreground">
                      Automatically send summaries every Monday
                    </p>
                  </div>
                  <Switch
                    checked={summarySettings.auto_send_enabled}
                    onCheckedChange={(checked) =>
                      setSummarySettings({ ...summarySettings, auto_send_enabled: checked })
                    }
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Send Time</Label>
                    <Input
                      type="time"
                      value={summarySettings.auto_send_time}
                      onChange={(e) => setSummarySettings({ ...summarySettings, auto_send_time: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Send Day</Label>
                    <Select
                      value={summarySettings.auto_send_day}
                      onValueChange={(v) => setSummarySettings({ ...summarySettings, auto_send_day: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monday">Monday</SelectItem>
                        <SelectItem value="sunday">Sunday</SelectItem>
                        <SelectItem value="friday">Friday</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-4 pt-4 border-t">
                  <p className="font-medium">Delivery Options</p>
                  
                  <div className="flex items-center justify-between">
                    <Label>Include Safety Documents</Label>
                    <Switch
                      checked={summarySettings.include_safety_docs}
                      onCheckedChange={(checked) =>
                        setSummarySettings({ ...summarySettings, include_safety_docs: checked })
                      }
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <Label>Require Acknowledgment</Label>
                    <Switch
                      checked={summarySettings.require_acknowledgment}
                      onCheckedChange={(checked) =>
                        setSummarySettings({ ...summarySettings, require_acknowledgment: checked })
                      }
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <Label>Email Delivery</Label>
                    <Switch
                      checked={summarySettings.email_delivery}
                      onCheckedChange={(checked) =>
                        setSummarySettings({ ...summarySettings, email_delivery: checked })
                      }
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <Label>In-App Notification</Label>
                    <Switch
                      checked={summarySettings.in_app_delivery}
                      onCheckedChange={(checked) =>
                        setSummarySettings({ ...summarySettings, in_app_delivery: checked })
                      }
                    />
                  </div>
                </div>

                <Button onClick={handleSaveSummarySettings} disabled={savingSettings}>
                  {savingSettings && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Save Settings
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Schedule Job Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Schedule Job</DialogTitle>
            <DialogDescription>
              Create a job from an approved quote or as a standalone job
            </DialogDescription>
          </DialogHeader>
          
          {/* Mode Selection Tabs */}
          <Tabs value={jobCreationMode} onValueChange={setJobCreationMode} className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="quote" data-testid="tab-from-quote">
                From Approved Quote
              </TabsTrigger>
              <TabsTrigger value="standalone" data-testid="tab-standalone">
                Standalone Job
              </TabsTrigger>
            </TabsList>
            
            <div className="space-y-4 max-h-[55vh] overflow-y-auto mt-4 pr-2">
              {/* From Quote Mode */}
              <TabsContent value="quote" className="space-y-4 mt-0">
                <div className="space-y-2">
                  <Label>Select Approved Quote *</Label>
                  <Select value={selectedQuote} onValueChange={setSelectedQuote}>
                    <SelectTrigger data-testid="schedule-quote-select">
                      <SelectValue placeholder="Select a quote" />
                    </SelectTrigger>
                    <SelectContent>
                      {quotes.filter((q) => q.status === "approved").length === 0 ? (
                        <SelectItem value="no-quotes" disabled>
                          No approved quotes available
                        </SelectItem>
                      ) : (
                        quotes
                          .filter((q) => q.status === "approved")
                          .map((q) => (
                            <SelectItem key={q.id} value={q.id}>
                              {q.quote_number} - {q.property_address || q.description?.substring(0, 30)}
                            </SelectItem>
                          ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </TabsContent>
              
              {/* Standalone Job Mode */}
              <TabsContent value="standalone" className="space-y-4 mt-0">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2 col-span-2">
                    <Label>Job Title *</Label>
                    <Input
                      value={jobTitle}
                      onChange={(e) => setJobTitle(e.target.value)}
                      placeholder="e.g., Window Cleaning - Main Building"
                      data-testid="job-title-input"
                    />
                  </div>
                  
                  <div className="space-y-2 col-span-2">
                    <Label>Description</Label>
                    <Textarea
                      value={jobDescription}
                      onChange={(e) => setJobDescription(e.target.value)}
                      placeholder="Detailed description of the work to be done..."
                      rows={2}
                    />
                  </div>
                  
                  <div className="space-y-2 col-span-2">
                    <Label>Job Address</Label>
                    <Input
                      value={jobAddress}
                      onChange={(e) => setJobAddress(e.target.value)}
                      placeholder="123 Main St, City, Province"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Client</Label>
                    <Select value={selectedClient || "none"} onValueChange={(v) => setSelectedClient(v === "none" ? "" : v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select client (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {clients.map((client) => (
                          <SelectItem key={client.id} value={client.id}>
                            {client.first_name} {client.last_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Company</Label>
                    <Select value={selectedCompany || "none"} onValueChange={(v) => setSelectedCompany(v === "none" ? "" : v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select company (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {companies.map((company) => (
                          <SelectItem key={company.id} value={company.id}>
                            {company.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Priority</Label>
                    <Select value={jobPriority} onValueChange={setJobPriority}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="normal">Normal</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="urgent">Urgent</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Estimated Hours</Label>
                    <Input
                      type="number"
                      step="0.5"
                      min="0"
                      value={estimatedHours}
                      onChange={(e) => setEstimatedHours(e.target.value)}
                      placeholder="e.g., 4"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Estimated Cost ($)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={estimatedCost}
                      onChange={(e) => setEstimatedCost(e.target.value)}
                      placeholder="e.g., 500.00"
                    />
                  </div>
                </div>
                
                {/* Recurring Job Section */}
                <div className="border-t pt-4 mt-4">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <Label className="text-base font-medium">Recurring Job</Label>
                      <p className="text-xs text-muted-foreground">
                        Set up automatic repeat scheduling
                      </p>
                    </div>
                    <Switch
                      checked={isRecurring}
                      onCheckedChange={setIsRecurring}
                      data-testid="recurring-toggle"
                    />
                  </div>
                  
                  {isRecurring && (
                    <div className="grid grid-cols-2 gap-4 animate-in fade-in">
                      <div className="space-y-2">
                        <Label>Repeat</Label>
                        <Select value={recurrencePattern} onValueChange={setRecurrencePattern}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="daily">Daily</SelectItem>
                            <SelectItem value="weekly">Weekly</SelectItem>
                            <SelectItem value="biweekly">Every 2 Weeks</SelectItem>
                            <SelectItem value="monthly">Monthly</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Generate Occurrences</Label>
                        <Select 
                          value={String(generateOccurrences)} 
                          onValueChange={(v) => setGenerateOccurrences(parseInt(v))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="4">4 occurrences</SelectItem>
                            <SelectItem value="8">8 occurrences</SelectItem>
                            <SelectItem value="12">12 occurrences</SelectItem>
                            <SelectItem value="26">26 occurrences (6 months)</SelectItem>
                            <SelectItem value="52">52 occurrences (1 year)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div className="col-span-2 space-y-2">
                        <Label>End Date (optional)</Label>
                        <CalendarComponent
                          mode="single"
                          selected={recurrenceEndDate}
                          onSelect={setRecurrenceEndDate}
                          className="rounded-md border"
                          disabled={(date) => date < new Date()}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </TabsContent>
              
              {/* Common Fields for Both Modes */}
              <div className="space-y-4 pt-4 border-t">
                <div className="space-y-2">
                  <Label>Proposed Dates *</Label>
                  <CalendarComponent
                    mode="multiple"
                    selected={proposedDates}
                    onSelect={setProposedDates}
                    className="rounded-md border"
                    disabled={(date) => date < new Date()}
                  />
                  {proposedDates.length > 0 && (
                    <p className="text-xs text-muted-foreground">
                      Selected: {proposedDates.map(d => d.toLocaleDateString()).join(", ")}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Assign Employees</Label>
                  <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto p-2 border rounded-md">
                    {employees.length === 0 ? (
                      <p className="text-sm text-muted-foreground col-span-2">No employees available</p>
                    ) : (
                      employees.map((emp) => (
                        <div
                          key={emp.id}
                          className="flex items-center space-x-2"
                        >
                          <Checkbox
                            id={`emp-${emp.id}`}
                            checked={selectedEmployees.includes(emp.id)}
                            onCheckedChange={() => toggleEmployee(emp.id)}
                          />
                          <Label htmlFor={`emp-${emp.id}`} className="text-sm cursor-pointer">
                            {emp.name}
                          </Label>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Any special instructions or notes..."
                    rows={2}
                  />
                </div>
              </div>
            </div>
          </Tabs>
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateSchedule}
              disabled={saving}
              data-testid="confirm-schedule-btn"
            >
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {jobCreationMode === "quote" ? "Create Schedule" : "Create Job"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sync Settings Dialog */}
      <Dialog open={syncSettingsOpen} onOpenChange={setSyncSettingsOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Calendar Sync Settings</DialogTitle>
            <DialogDescription>
              Enter your calendar API credentials to enable sync. You can get these from the Google Cloud Console or Azure Portal.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Info */}
            <div className="p-4 bg-muted/50 rounded-lg text-sm">
              {isOfficeManager ? (
                <p>As an Office Manager, your calendar will sync <strong>all scheduled jobs</strong>.</p>
              ) : (
                <p>Your calendar will sync only <strong>jobs assigned to you</strong>.</p>
              )}
            </div>

            {/* Google Calendar Credentials */}
            <div className="p-4 border rounded-lg space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-red-100 dark:bg-red-900/20 flex items-center justify-center">
                  <svg className="w-6 h-6" viewBox="0 0 24 24">
                    <path fill="#EA4335" d="M12 11.5v5h7.5a9.5 9.5 0 1 1-2.5-8.5l3.5-3.5A14 14 0 1 0 12 24c7.5 0 14-6 14-12.5H12z"/>
                  </svg>
                </div>
                <div>
                  <p className="font-medium">Google Calendar</p>
                  <p className="text-xs text-muted-foreground">Get credentials from console.cloud.google.com</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm">Client ID</Label>
                  <Input
                    value={syncCredentials.google_client_id}
                    onChange={(e) => setSyncCredentials({...syncCredentials, google_client_id: e.target.value})}
                    placeholder="your-client-id.apps.googleusercontent.com"
                    data-testid="google-client-id"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Client Secret</Label>
                  <Input
                    type="password"
                    value={syncCredentials.google_client_secret}
                    onChange={(e) => setSyncCredentials({...syncCredentials, google_client_secret: e.target.value})}
                    placeholder="Client secret"
                    data-testid="google-client-secret"
                  />
                </div>
              </div>
              {syncCredentials.google_client_id && (
                <Button size="sm" onClick={connectGoogleCalendar}>
                  <Link className="h-4 w-4 mr-2" />
                  Connect Google Calendar
                </Button>
              )}
            </div>

            {/* Outlook Calendar Credentials */}
            <div className="p-4 border rounded-lg space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/20 flex items-center justify-center">
                  <svg className="w-6 h-6" viewBox="0 0 24 24">
                    <path fill="#0078D4" d="M24 7.387v10.478c0 .23-.08.424-.238.576a.806.806 0 0 1-.595.234h-8.167v-6.28l1.602 1.172a.452.452 0 0 0 .27.089.452.452 0 0 0 .269-.089.335.335 0 0 0 .133-.27V7.387h6.488c.23 0 .426.078.595.234.158.152.238.346.238.576zM15 14.395v-3.047l-3 2.198-3-2.198v3.047h6zm-7.167 4.28H.833a.806.806 0 0 1-.595-.234A.778.778 0 0 1 0 17.865V7.387c0-.23.08-.424.238-.576a.806.806 0 0 1 .595-.234h6.488v5.91a.335.335 0 0 0 .133.27.452.452 0 0 0 .27.089.452.452 0 0 0 .269-.089l1.602-1.172v6.28H7.833z"/>
                  </svg>
                </div>
                <div>
                  <p className="font-medium">Outlook Calendar</p>
                  <p className="text-xs text-muted-foreground">Get credentials from portal.azure.com</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm">Application (Client) ID</Label>
                  <Input
                    value={syncCredentials.outlook_client_id}
                    onChange={(e) => setSyncCredentials({...syncCredentials, outlook_client_id: e.target.value})}
                    placeholder="Application (client) ID"
                    data-testid="outlook-client-id"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Client Secret</Label>
                  <Input
                    type="password"
                    value={syncCredentials.outlook_client_secret}
                    onChange={(e) => setSyncCredentials({...syncCredentials, outlook_client_secret: e.target.value})}
                    placeholder="Client secret"
                    data-testid="outlook-client-secret"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-sm">Tenant ID</Label>
                <Input
                  value={syncCredentials.outlook_tenant_id}
                  onChange={(e) => setSyncCredentials({...syncCredentials, outlook_tenant_id: e.target.value})}
                  placeholder="common (or your tenant ID)"
                  data-testid="outlook-tenant-id"
                />
              </div>
              {syncCredentials.outlook_client_id && (
                <Button size="sm" onClick={connectOutlookCalendar}>
                  <Link className="h-4 w-4 mr-2" />
                  Connect Outlook Calendar
                </Button>
              )}
            </div>

            {/* Manual Export */}
            <div className="p-4 border rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Manual Export (ICS)</p>
                  <p className="text-sm text-muted-foreground">
                    Download and import into any calendar app
                  </p>
                </div>
                <Button variant="outline" onClick={handleExportICS}>
                  <Download className="h-4 w-4 mr-2" />
                  Download
                </Button>
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setSyncSettingsOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveSyncCredentials} disabled={savingSyncSettings}>
              {savingSyncSettings && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save Credentials
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Work Notice Dialog */}
      <Dialog open={noticeDialogOpen} onOpenChange={setNoticeDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Generate Work Notice</DialogTitle>
          </DialogHeader>
          
          {loadingNotice ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : noticePreview ? (
            <div className="space-y-4">
              <div className="text-sm space-y-1">
                <p><strong>Recipient:</strong> {noticePreview.recipient_email || "No email found"}</p>
                <p><strong>Template:</strong> {noticePreview.template_name}</p>
              </div>
              
              <div className="p-4 bg-muted/50 rounded-lg max-h-64 overflow-y-auto">
                <pre className="text-sm whitespace-pre-wrap font-mono">
                  {noticePreview.preview_content}
                </pre>
              </div>
              
              {!noticePreview.can_send && (
                <p className="text-sm text-amber-600">
                  No recipient email found. Add an email to the property manager or company to send.
                </p>
              )}
            </div>
          ) : (
            <p className="text-muted-foreground">Failed to generate notice</p>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setNoticeDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSendNotice}
              disabled={sendingNotice || !noticePreview?.can_send}
            >
              {sendingNotice && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              <Send className="h-4 w-4 mr-2" />
              Send Notice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Complete Job Dialog */}
      <Dialog open={completeDialogOpen} onOpenChange={setCompleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Complete Job</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Actual Hours Worked</Label>
              <Input
                type="number"
                step="0.5"
                min="0"
                value={actualHours}
                onChange={(e) => setActualHours(parseFloat(e.target.value) || 0)}
              />
            </div>
            
            <div className="flex items-center space-x-2">
              <Checkbox
                id="auto-update"
                checked={autoUpdateQuote}
                onCheckedChange={setAutoUpdateQuote}
              />
              <Label htmlFor="auto-update" className="text-sm font-normal">
                Automatically update quote with actual hours
              </Label>
            </div>
            
            <p className="text-sm text-muted-foreground">
              This will mark the job as complete and update the quote status to &quot;Completed&quot;.
            </p>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCompleteJob} disabled={completing}>
              {completing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              <CheckCircle className="h-4 w-4 mr-2" />
              Mark Complete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Job Dialog */}
      <Dialog open={editJobDialogOpen} onOpenChange={setEditJobDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Scheduled Job</DialogTitle>
            <DialogDescription>Update job details</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Job Title</Label>
              <Input
                value={editJobForm.title}
                onChange={(e) => setEditJobForm({...editJobForm, title: e.target.value})}
                data-testid="edit-job-title"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={editJobForm.description}
                onChange={(e) => setEditJobForm({...editJobForm, description: e.target.value})}
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Scheduled Date</Label>
                <Input
                  type="date"
                  value={editJobForm.scheduled_date}
                  onChange={(e) => setEditJobForm({...editJobForm, scheduled_date: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select value={editJobForm.priority} onValueChange={(v) => setEditJobForm({...editJobForm, priority: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Address</Label>
                <Input
                  value={editJobForm.address}
                  onChange={(e) => setEditJobForm({...editJobForm, address: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Est. Hours</Label>
                <Input
                  type="number"
                  step="0.5"
                  value={editJobForm.estimated_hours}
                  onChange={(e) => setEditJobForm({...editJobForm, estimated_hours: e.target.value})}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={editJobForm.notes}
                onChange={(e) => setEditJobForm({...editJobForm, notes: e.target.value})}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <div className="flex gap-2 mr-auto">
              <Button
                type="button"
                variant="outline"
                size="sm"
                data-testid="add-to-google-calendar-btn"
                disabled={!editJobForm.scheduled_date || !editJobForm.title}
                onClick={() => {
                  const start = new Date(`${editJobForm.scheduled_date}T09:00:00`);
                  const hours = parseFloat(editJobForm.estimated_hours) || 1;
                  const end = new Date(start.getTime() + hours * 60 * 60 * 1000);
                  const url = googleCalendarUrl({
                    title: editJobForm.title,
                    description: editJobForm.description || editJobForm.notes,
                    location: editJobForm.address,
                    start,
                    end,
                  });
                  window.open(url, "_blank", "noopener,noreferrer");
                }}
              >
                <CalendarIcon className="h-3.5 w-3.5 mr-1.5" />
                Add to Google
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                data-testid="add-to-outlook-btn"
                disabled={!editJobForm.scheduled_date || !editJobForm.title}
                onClick={() => {
                  const start = new Date(`${editJobForm.scheduled_date}T09:00:00`);
                  const hours = parseFloat(editJobForm.estimated_hours) || 1;
                  const end = new Date(start.getTime() + hours * 60 * 60 * 1000);
                  const url = outlookCalendarUrl({
                    title: editJobForm.title,
                    description: editJobForm.description || editJobForm.notes,
                    location: editJobForm.address,
                    start,
                    end,
                  });
                  window.open(url, "_blank", "noopener,noreferrer");
                }}
              >
                <CalendarIcon className="h-3.5 w-3.5 mr-1.5" />
                Add to Outlook
              </Button>
            </div>
            <Button variant="outline" onClick={() => setEditJobDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveEditJob} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Weekly Summary Preview Dialog */}
      <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Weekly Job Summary Preview</DialogTitle>
          </DialogHeader>
          {previewData && (
            <div className="space-y-6">
              {/* Employee Info */}
              <div className="flex items-center gap-4 p-4 bg-muted rounded-lg">
                <Users className="h-8 w-8 text-primary" />
                <div>
                  <p className="font-semibold">{previewData.employee?.name}</p>
                  <p className="text-sm text-muted-foreground">
                    Week of {previewData.summary?.week_start} to {previewData.summary?.week_end}
                  </p>
                </div>
                {previewData.summary?.status && getSummaryStatusBadge(previewData.summary.status)}
              </div>

              {/* Schedules */}
              <div>
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <CalendarIcon className="h-4 w-4" />
                  Scheduled Jobs ({previewData.schedules?.length || 0})
                </h4>
                {previewData.schedules?.length > 0 ? (
                  <div className="space-y-2">
                    {previewData.schedules.map((schedule, idx) => (
                      <div key={idx} className="p-3 border rounded-lg">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium">{schedule.date}</p>
                            <p className="text-sm text-muted-foreground">
                              {schedule.start_time} - {schedule.end_time}
                            </p>
                          </div>
                          <Badge variant="outline">{schedule.status || "Scheduled"}</Badge>
                        </div>
                        {schedule.address && (
                          <p className="text-sm mt-1">
                            {schedule.address.building_name || schedule.address.street_address}
                          </p>
                        )}
                        {schedule.description && (
                          <p className="text-sm text-muted-foreground mt-1">{schedule.description}</p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground">No jobs scheduled for this week</p>
                )}
              </div>

              {/* Safety Documents */}
              <div>
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <Shield className="h-4 w-4" />
                  Safety Documents ({previewData.safety_documents?.length || 0})
                </h4>
                {previewData.safety_documents?.length > 0 ? (
                  <div className="space-y-2">
                    {previewData.safety_documents.map((doc, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 border rounded-lg">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-muted-foreground" />
                          <span>{doc.name}</span>
                          <Badge variant="outline" className="ml-2">{doc.category}</Badge>
                        </div>
                        {doc.requires_acknowledgment && (
                          <Badge className="bg-yellow-100 text-yellow-800">
                            Acknowledgment Required
                          </Badge>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground">No safety documents attached</p>
                )}
              </div>

              {/* Notes */}
              {previewData.summary?.notes && (
                <div>
                  <h4 className="font-semibold mb-2">Notes</h4>
                  <p className="text-sm p-3 bg-muted rounded-lg">{previewData.summary.notes}</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewDialogOpen(false)}>
              Close
            </Button>
            {previewData?.summary?.id && (
              <Button onClick={() => handleDownloadPDF(previewData.summary.id)}>
                <Download className="h-4 w-4 mr-2" />
                Download PDF
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Weekly Summary Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Summary Notes</DialogTitle>
            <DialogDescription>
              Add notes before sending to the employee
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Textarea
              value={editNotes}
              onChange={(e) => setEditNotes(e.target.value)}
              placeholder="Add any notes or special instructions..."
              rows={5}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveEdit}>
              <CheckCircle className="h-4 w-4 mr-2" />
              Save Notes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Calendar;
